/**
 * marsco开户没有流动净资产、净资产、年净收入、投资经验最大最小值的限制条件
 */

import { FC, useMemo, useState } from 'react';
import { $Formutil, connect } from 'react-bootstrap-formutil';
import FormGroup from 'components/FormGroup';
import FormSelect, { transformLegacyOptions } from 'components/FormSelect';
import type { CustomerInfoKeyType } from 'types/customer';
import config from 'utils/config';
import { getMaxMessage, getMinMessage, getMinTradeMessage, tradeLevel, tradeNumber, tradeYearOptions } from './helper';

type TradeProductName = 'stk' | 'opt';

export type InvestInfoFormutil = Record<
    `${TradeProductName}_trade_year` | `${TradeProductName}_trade_per_year` | `${TradeProductName}_kl`,
    string
>;

interface InvestInfoProps {
    $formutil: $Formutil<InvestInfoFormutil>;
    disabled?: boolean;
    name?: TradeProductName;
    age: number;
    minTradeYears: number;
    isOption: boolean;
    isMarsco?: boolean;
}

export function getDefaultFieldsOfInvestInfo(name: TradeProductName): CustomerInfoKeyType[] {
    return [`${name}_trade_year`, `${name}_trade_per_year`, `${name}_kl`];
}

const InvestInfo: FC<InvestInfoProps> = ({
    age,
    name = 'stk',
    minTradeYears = 1,
    disabled = false,
    isOption = false,
    isMarsco = false
}) => {
    const [maxTradeYears] = useState<number>(() => {
        // 根据年龄算出的最大交易年数限制
        const yearsDiff = age - 17;

        // 交易年数候选项后两个是范围区间，所以需要特殊处理下
        return yearsDiff > 6 && yearsDiff < 10 ? 6 : yearsDiff;
    });
    const validMessageType = useMemo(() => (isOption ? __('option') : __('stock')), [isOption]);
    const { tradeYearOtherProps, tradePerYearOtherProps, knowledgeLevelOtherProps } = useMemo(() => {
        const tradeYearOtherProps = isMarsco
            ? {}
            : {
                  min: getMinTradeMessage(validMessageType),
                  max: getMaxMessage(validMessageType)
              };
        const tradePerYearOtherProps = isMarsco
            ? {}
            : {
                  min: getMinMessage(validMessageType)
              };
        const knowledgeLevelOtherProps = isMarsco
            ? {}
            : {
                  min: getMinMessage(validMessageType)
              };

        return {
            tradeYearOtherProps,
            tradePerYearOtherProps,
            knowledgeLevelOtherProps
        };
    }, [isMarsco, validMessageType]);

    return (
        <div>
            <FormGroup
                name={`${name}_trade_year`}
                label={__('Years of Trading')}
                min={isMarsco ? null : minTradeYears}
                max={isMarsco ? null : maxTradeYears}
                required
                validMessage={{
                    required: config.selectError(__('years of trading')),
                    ...tradeYearOtherProps
                }}>
                <FormSelect placeholder={__('Please Select')} disabled={disabled} options={tradeYearOptions} />
            </FormGroup>
            <FormGroup
                name={`${name}_trade_per_year`}
                label={__('Trades Per Year')}
                min={isMarsco ? null : 11}
                required
                validMessage={{
                    required: config.selectError(__('trades per year')),
                    ...tradePerYearOtherProps
                }}>
                <FormSelect
                    placeholder={__('Please Select')}
                    disabled={disabled}
                    options={transformLegacyOptions(tradeNumber, `${name}_trade_per_year`, {
                        labelName: 'trades',
                        valueName: 'value'
                    })}
                />
            </FormGroup>
            <FormGroup
                name={`${name}_kl`}
                label={__('Knowledge Level')}
                min={isMarsco ? null : 1}
                required
                validMessage={{
                    required: config.selectError(__('knowledge level')),
                    ...knowledgeLevelOtherProps
                }}>
                <FormSelect
                    placeholder={__('Please Select')}
                    disabled={disabled}
                    options={transformLegacyOptions(tradeLevel, `${name}_kl`)}
                />
            </FormGroup>
        </div>
    );
};

export default connect(InvestInfo);
